import {
  Alert,
  Button,
  Dropdown,
  Empty,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableProps } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAssetLibrary } from '@/hooks/useAssetLibrary';
import {
  DeleteIcon as DeleteOutlined,
  HorizontalEllipsisIcon as EllipsisOutlined,
  FilterFilledIcon as FilterFilled,
  AssetLibraryIcon as FolderOpenOutlined,
  LinkIcon as LinkOutlined,
  AddIcon as PlusOutlined,
  SearchIcon as SearchOutlined,
} from '@/icons';
import { formatAssetDate, type AssetLibraryTagOption } from '@/models/asset-library.model';
import { getTypeBadge } from '@/models/knowledge-card-badge';
import { KnowledgeCard } from '@/models/knowledge-card.model';

const { Text, Title } = Typography;

const TYPE_FILTER_ALL = '__all__';

/** Keep keystrokes inside column filter UIs from bubbling to the Ant Design Table. */
const stopTableFilterKeyPropagation = (event: React.KeyboardEvent): void => {
  event.stopPropagation();
};

const assetTitleSearchFilterIcon = (filtered: boolean) => (
  <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} aria-hidden />
);

const assetTypeFilterIcon = (filtered: boolean) => (
  <FilterFilled style={{ color: filtered ? '#1677ff' : undefined }} aria-hidden />
);

type TagOption = AssetLibraryTagOption;

/** Custom header filter: search (`q`) + optional tag (server `tag` param). */
const AssetNameFilterDropdown: React.FC<{
  tagOptions: TagOption[];
  initialQ: string;
  initialTag: string;
  onApply: (q: string, tag: string) => void;
  onReset: () => void;
  confirm: () => void;
  close: (() => void) | undefined;
}> = ({ tagOptions, initialQ, initialTag, onApply, onReset, confirm, close }) => {
  const [draftQ, setDraftQ] = useState(initialQ);
  const [draftTag, setDraftTag] = useState<string | undefined>(initialTag || undefined);

  useEffect(() => {
    setDraftQ(initialQ);
    setDraftTag(initialTag || undefined);
  }, [initialQ, initialTag]);

  const finish = useCallback(() => {
    confirm();
    close?.();
  }, [confirm, close]);

  return (
    <dialog
      open
      aria-label="Filter assets by name or tag"
      tabIndex={-1}
      style={{
        padding: 8,
        width: 260,
        margin: 0,
        border: 'none',
        maxWidth: 'none',
        background: 'transparent',
      }}
    >
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
        Search title or content
      </Text>
      <Input
        allowClear
        placeholder="Search…"
        value={draftQ}
        onChange={(e) => setDraftQ(e.target.value)}
        onKeyDown={stopTableFilterKeyPropagation}
        onPressEnter={() => {
          onApply(draftQ, draftTag ?? '');
          finish();
        }}
      />
      <Text
        type="secondary"
        style={{ fontSize: 12, display: 'block', marginTop: 8, marginBottom: 4 }}
      >
        Tag
      </Text>
      <Select
        allowClear
        showSearch
        placeholder="Any tag"
        style={{ width: '100%' }}
        options={tagOptions}
        value={draftTag}
        onChange={(v) => setDraftTag(v)}
        onKeyDown={stopTableFilterKeyPropagation}
        optionFilterProp="label"
      />
      <Space style={{ marginTop: 8 }}>
        <Button
          type="primary"
          size="small"
          onKeyDown={stopTableFilterKeyPropagation}
          onClick={() => {
            onApply(draftQ, draftTag ?? '');
            finish();
          }}
        >
          Apply
        </Button>
        <Button
          size="small"
          onKeyDown={stopTableFilterKeyPropagation}
          onClick={() => {
            setDraftQ('');
            setDraftTag(undefined);
            onReset();
            finish();
          }}
        >
          Reset
        </Button>
      </Space>
    </dialog>
  );
};

const AssetTableNameFilterDropdown: React.FC<{
  tagOptions: TagOption[];
  filterQuery: string | null;
  filterTag: string | null;
  setFilterQuery: (v: string | null) => void;
  setFilterTag: (v: string | null) => void;
  confirm: () => void;
  close: (() => void) | undefined;
}> = ({ tagOptions, filterQuery, filterTag, setFilterQuery, setFilterTag, confirm, close }) => (
  <AssetNameFilterDropdown
    tagOptions={tagOptions}
    initialQ={filterQuery ?? ''}
    initialTag={filterTag ?? ''}
    onApply={(q, tag) => {
      setFilterQuery(q.trim() || null);
      setFilterTag(tag.trim() || null);
    }}
    onReset={() => {
      setFilterQuery(null);
      setFilterTag(null);
    }}
    confirm={confirm}
    close={close}
  />
);

const SourceTypeTableFilterDropdown: React.FC<{
  filterSourceType: string | null;
  setFilterSourceType: (v: string | null) => void;
  confirm: () => void;
  close: (() => void) | undefined;
}> = ({ filterSourceType, setFilterSourceType, confirm, close }) => (
  <dialog
    open
    aria-label="Filter by asset type"
    tabIndex={-1}
    style={{
      padding: 8,
      margin: 0,
      border: 'none',
      maxWidth: 'none',
      background: 'transparent',
    }}
  >
    <Radio.Group
      value={filterSourceType ?? TYPE_FILTER_ALL}
      onChange={(e) => {
        const v = e.target.value as string;
        setFilterSourceType(v === TYPE_FILTER_ALL ? null : v);
        confirm();
        close?.();
      }}
    >
      <Space direction="vertical" size={4}>
        <Radio value={TYPE_FILTER_ALL} onKeyDown={stopTableFilterKeyPropagation}>
          All types
        </Radio>
        <Radio value="note" onKeyDown={stopTableFilterKeyPropagation}>
          Note
        </Radio>
        <Radio value="document" onKeyDown={stopTableFilterKeyPropagation}>
          Document
        </Radio>
        <Radio value="file_url" onKeyDown={stopTableFilterKeyPropagation}>
          File / URL
        </Radio>
        <Radio value="markdown_import" onKeyDown={stopTableFilterKeyPropagation}>
          Markdown import
        </Radio>
      </Space>
    </Radio.Group>
  </dialog>
);

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface AssetLibraryPageProps {
  clientId: string;
  projectId?: string;
  accessToken?: string | null;
  /** Navigate to an asset detail view by card ID. */
  onOpenAsset?: (cardId: string) => void;
  /** Open the Add Asset modal. */
  onOpenAddAsset?: () => void;
}

type AssetLibraryColumnOptions = {
  filterQuery: string | null;
  filterTag: string | null;
  filterSourceType: string | null;
  tagOptions: TagOption[];
  setFilterQuery: (v: string | null) => void;
  setFilterTag: (v: string | null) => void;
  setFilterSourceType: (v: string | null) => void;
  handleOpenCard: (card: KnowledgeCard) => void;
  handleViewCard: (card: KnowledgeCard) => void;
  handleViewOriginal: (card: KnowledgeCard) => void;
  handleCopyLink: (card: KnowledgeCard) => void;
  handleDeleteCard: (card: KnowledgeCard) => void;
};

function buildAssetLibraryTableColumns(
  options: AssetLibraryColumnOptions,
): NonNullable<TableProps<KnowledgeCard>['columns']> {
  const {
    filterQuery,
    filterTag,
    filterSourceType,
    tagOptions,
    setFilterQuery,
    setFilterTag,
    setFilterSourceType,
    handleOpenCard,
    handleViewCard,
    handleViewOriginal,
    handleCopyLink,
    handleDeleteCard,
  } = options;

  return [
    {
      title: 'Asset Name',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
      ellipsis: true,
      filteredValue: filterQuery || filterTag ? ['1'] : null,
      filterIcon: assetTitleSearchFilterIcon,
      filterDropdown: ({ confirm, close }) => (
        <AssetTableNameFilterDropdown
          tagOptions={tagOptions}
          filterQuery={filterQuery}
          filterTag={filterTag}
          setFilterQuery={setFilterQuery}
          setFilterTag={setFilterTag}
          confirm={confirm}
          close={close}
        />
      ),
      render: (_value: unknown, record: KnowledgeCard) => {
        const subtitle = record.getSubtitle();
        const tagLabels = record.tags.filter((t) => t.trim().length > 0);
        const showOriginalLink = record.sourceType === 'document' && Boolean(record.sourceUrl);
        return (
          <div>
            <Text strong style={{ display: 'block' }}>
              {record.title}
            </Text>
            {subtitle && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {subtitle}
              </Text>
            )}
            {tagLabels.length > 0 && (
              <Space size={[4, 4]} wrap style={{ marginTop: subtitle ? 4 : 6 }}>
                {tagLabels.map((tag) => (
                  <Tag key={tag} style={{ margin: 0, fontSize: 11 }}>
                    {tag}
                  </Tag>
                ))}
                {showOriginalLink && (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', fontSize: 11 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewOriginal(record);
                    }}
                  >
                    Original Document
                  </Button>
                )}
              </Space>
            )}
            {tagLabels.length === 0 && showOriginalLink && (
              <div style={{ marginTop: subtitle ? 4 : 6 }}>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto', fontSize: 11 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewOriginal(record);
                  }}
                >
                  Original Document
                </Button>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Type',
      key: 'source_type',
      width: 136,
      align: 'center' as const,
      sorter: (a, b) => a.sourceType.localeCompare(b.sourceType),
      filteredValue: filterSourceType ? [filterSourceType] : null,
      filterIcon: assetTypeFilterIcon,
      filterDropdown: ({ confirm, close }) => (
        <SourceTypeTableFilterDropdown
          filterSourceType={filterSourceType}
          setFilterSourceType={setFilterSourceType}
          confirm={confirm}
          close={close}
        />
      ),
      render: (_value: unknown, record: KnowledgeCard) => {
        const badge = getTypeBadge(record);
        return (
          <Tag
            className="asset-library-table__type-badge"
            color={badge.color}
            aria-label={badge.label}
          >
            {badge.label}
          </Tag>
        );
      },
    },
    {
      title: 'Uploaded',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
      defaultSortOrder: 'descend' as const,
      render: (value: string) => formatAssetDate(value),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (_value: unknown, record: KnowledgeCard) => (
        <Space
          size="small"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Button
            type="link"
            size="small"
            aria-label={`View ${record.title}`}
            onClick={() => handleViewCard(record)}
          >
            View
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'open-card',
                  label: 'Open card',
                  icon: <FolderOpenOutlined />,
                  onClick: () => handleOpenCard(record),
                },
                ...(record.sourceType === 'document' && record.sourceUrl
                  ? [
                      {
                        key: 'view-original',
                        label: 'View original',
                        icon: <LinkOutlined />,
                        onClick: () => handleViewOriginal(record),
                      },
                    ]
                  : []),
                {
                  key: 'copy-link',
                  label: 'Copy link',
                  icon: <LinkOutlined />,
                  onClick: () => handleCopyLink(record),
                },
                { type: 'divider' },
                {
                  key: 'delete',
                  label: 'Delete',
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => handleDeleteCard(record),
                },
              ],
            }}
            trigger={['click']}
          >
            <Button
              type="text"
              size="small"
              icon={<EllipsisOutlined />}
              aria-label={`More actions for ${record.title}`}
            />
          </Dropdown>
        </Space>
      ),
    },
  ];
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const AssetLibraryPage: React.FC<AssetLibraryPageProps> = ({
  clientId,
  projectId,
  accessToken,
  onOpenAsset,
  onOpenAddAsset,
}) => {
  const {
    cards,
    total,
    totalLabel,
    page,
    pageSize,
    loading,
    error,
    filters,
    tagOptions,
    setFilterQuery,
    setFilterSourceType,
    setFilterTag,
    openCard,
    viewCard,
    openOriginal,
    copyLink,
    deleteCard,
    changePage,
    retry,
  } = useAssetLibrary({ clientId, projectId, accessToken, onOpenAsset });

  const handleCopyLink = useCallback(
    (card: KnowledgeCard) => {
      void copyLink(card).then((copied) => {
        void (copied ? message.success('Link copied') : message.error('Failed to copy link'));
      });
    },
    [copyLink],
  );

  const handleDeleteCard = useCallback(
    (card: KnowledgeCard) => {
      Modal.confirm({
        title: 'Delete this asset?',
        content: `This will permanently delete "${card.title}" and remove it from search results.`,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await deleteCard(card);
            void message.success('Asset deleted');
          } catch (error) {
            const description = error instanceof Error ? error.message : 'Failed to delete asset';
            void message.error(description);
          }
        },
      });
    },
    [deleteCard],
  );

  const handleTableChange = useCallback<NonNullable<TableProps<KnowledgeCard>['onChange']>>(
    (pagination, _filters, _sorter, extra) => {
      if (extra?.action === 'paginate') {
        const nextPage = pagination.current ?? 1;
        const nextSize = pagination.pageSize ?? pageSize;
        changePage(nextPage, nextSize);
      }
    },
    [changePage, pageSize],
  );

  /* ---- Column definitions ----------------------------------------- */

  const columns: TableProps<KnowledgeCard>['columns'] = useMemo(
    () =>
      buildAssetLibraryTableColumns({
        filterQuery: filters.query,
        filterTag: filters.tag,
        filterSourceType: filters.sourceType,
        tagOptions,
        setFilterQuery,
        setFilterTag,
        setFilterSourceType,
        handleOpenCard: openCard,
        handleViewCard: viewCard,
        handleViewOriginal: openOriginal,
        handleCopyLink,
        handleDeleteCard,
      }),
    [
      handleCopyLink,
      handleDeleteCard,
      filters,
      openCard,
      openOriginal,
      setFilterQuery,
      setFilterSourceType,
      setFilterTag,
      tagOptions,
      viewCard,
    ],
  );

  /* ---- Render ----------------------------------------------------- */

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Asset Library
          </Title>
          {!loading && total > 0 && <Text type="secondary">{totalLabel}</Text>}
        </div>
        {onOpenAddAsset && (
          <Button type="primary" icon={<PlusOutlined />} onClick={onOpenAddAsset}>
            Add Asset
          </Button>
        )}
      </div>

      {/* Error alert */}
      {error && (
        <Alert
          type="error"
          showIcon
          message="Failed to load assets"
          description={error}
          action={
            <Button size="small" onClick={retry}>
              Retry
            </Button>
          }
        />
      )}

      {/* Data table */}
      <Table<KnowledgeCard>
        className="asset-library-table"
        rowKey="cardId"
        aria-label="Asset library"
        dataSource={cards}
        columns={columns}
        loading={loading}
        tableLayout="fixed"
        scroll={{ x: 760 }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => openCard(record),
          style: { cursor: 'pointer' },
        })}
        locale={{
          emptyText: (
            <Empty
              description={
                <span>No assets yet. Click &quot;+ Add Asset&quot; to get started.</span>
              }
            />
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />
    </Space>
  );
};

export default AssetLibraryPage;
